// src/pages/RTDashboard.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { rtAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ClipboardCheck, FileText, CheckCircle, Clock, AlertTriangle, Eye, RefreshCw, Activity } from 'lucide-react';

const STATUS_META = {
  NOUVEAU:                            { label: 'Nouveau',    color: 'var(--text-muted)', bg: 'rgba(255,255,255,0.06)' },
  EN_COURS_DE_SOURCING:               { label: 'Sourcing',   color: '#F59E0B', bg: '#FFFBEB' },
  ENVOYE_A_EQUIPE_TALENT_ACQUISITION: { label: 'Envoyé TA',  color: '#A78BFA', bg: 'rgba(139,92,246,0.10)' },
  SHORT_LIST_ENVOYEE:                 { label: 'Shortlist',  color: '#FF4100', bg: 'var(--orange-50)' },
  SOURCING:                           { label: 'Sourcing',   color: '#F59E0B', bg: '#FFFBEB' },
  QUALIFICATION:                      { label: 'Qualification', color: '#EA580C', bg: '#FFF7ED' },
};

const REPORT_STATUS = {
  BROUILLON: { label: 'Brouillon', color: '#F59E0B', bg: '#FFFBEB' },
  SOUMIS:    { label: 'Soumis',    color: '#16A34A', bg: 'rgba(34,197,94,0.08)' },
};

export default function RTDashboard() {
  const navigate = useNavigate();
  const [myAOs,    setMyAOs]    = useState([]);
  const [myReports,setMyReports]= useState([]);
  const [loading,  setLoading]  = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [aosRes, reportsRes] = await Promise.all([
        rtAPI.getMyAOs(),
        rtAPI.getMyReports(),
      ]);
      setMyAOs(aosRes.data || []);
      setMyReports(reportsRes.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const pendingSoft = myReports.filter(r => r.report_type === 'SOFT_SKILLS' && r.status === 'BROUILLON').length;
  const pendingHard = myReports.filter(r => r.report_type === 'HARD_SKILLS' && r.status === 'BROUILLON').length;
  const submitted   = myReports.filter(r => r.status === 'SOUMIS').length;

  // Construire le flux d'activité à partir des rapports
  const activityFeed = useMemo(() => {
    const events = [];
    myReports.forEach(r => {
      const typeLabel = r.report_type === 'SOFT_SKILLS' ? 'Soft Skills' : 'Hard Skills';
      if (r.created_at) {
        events.push({ date: r.created_at, type: 'created', label: `Rapport ${typeLabel} créé`, aoId: r.ao_id, reportId: r.id, candidateId: r.candidate_id, status: r.status });
      }
      if (r.status === 'SOUMIS' && r.submitted_at) {
        events.push({ date: r.submitted_at, type: 'submitted', label: `Rapport ${typeLabel} soumis`, aoId: r.ao_id, reportId: r.id, candidateId: r.candidate_id, status: r.status, note: r.note_sur_10 });
      }
    });
    return events.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 30);
  }, [myReports]);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Mes évaluations</h1>
          <p>AOs assignés et rapports à compléter</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchAll}><RefreshCw size={15}/></button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
        {[
          { label:'AOs assignés',      value: myAOs.length,  color:'#FF4100', bg:'var(--orange-50)', icon: ClipboardCheck },
          { label:'Soft skills en cours', value: pendingSoft, color:'#F59E0B', bg:'#FFFBEB', icon: Clock },
          { label:'Hard skills en cours', value: pendingHard, color:'#EA580C', bg:'#FFF7ED', icon: AlertTriangle },
          { label:'Rapports soumis',    value: submitted,    color:'#16A34A', bg:'rgba(34,197,94,0.08)', icon: CheckCircle },
        ].map(({ label, value, color, bg, icon: Icon }) => (
          <div key={label} className="card" style={{ padding:'18px 20px', borderLeft:`4px solid ${color}` }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <span style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</span>
              <Icon size={16} color={color}/>
            </div>
            <div style={{ fontSize:'1.75rem', fontWeight:800, color }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
        {/* AOs assignés */}
        <div>
          <h2 style={{ fontSize:'0.9375rem', fontWeight:700, marginBottom:12 }}>AOs assignés</h2>
          {loading ? (
            <div className="card" style={{ padding:32, textAlign:'center' }}><div className="spinner"/></div>
          ) : myAOs.length === 0 ? (
            <div className="card" style={{ padding:32, textAlign:'center', color:'var(--text-muted)', fontSize:'0.875rem' }}>
              <ClipboardCheck size={32} style={{ opacity:0.3, marginBottom:8 }}/>
              <div>Aucun AO assigné pour l'instant</div>
            </div>
          ) : (
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>AO</th><th>Statut</th><th></th></tr>
                  </thead>
                  <tbody>
                    {myAOs.map(ao => {
                      const sm = STATUS_META[ao.ao_avancement] || { label: ao.ao_avancement, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                      return (
                        <tr key={ao.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/ao/${ao.id}`)}>
                          <td>
                            <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{ao.job_title || 'Sans titre'}</div>
                            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{ao.client || '—'}{ao.location ? ` · ${ao.location}` : ''}</div>
                          </td>
                          <td><span style={{ padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:sm.bg, color:sm.color }}>{sm.label}</span></td>
                          <td><button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); navigate(`/ao/${ao.id}`); }}><Eye size={13}/></button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Rapports récents */}
        <div>
          <h2 style={{ fontSize:'0.9375rem', fontWeight:700, marginBottom:12 }}>Mes rapports</h2>
          {loading ? (
            <div className="card" style={{ padding:32, textAlign:'center' }}><div className="spinner"/></div>
          ) : myReports.length === 0 ? (
            <div className="card" style={{ padding:32, textAlign:'center', color:'var(--text-muted)', fontSize:'0.875rem' }}>
              <FileText size={32} style={{ opacity:0.3, marginBottom:8 }}/>
              <div>Aucun rapport rédigé</div>
            </div>
          ) : (
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>Type</th><th>Note</th><th>Statut</th><th></th></tr>
                  </thead>
                  <tbody>
                    {myReports.map(r => {
                      const rs = REPORT_STATUS[r.status] || { label: r.status, color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                      return (
                        <tr key={r.id}>
                          <td>
                            <div style={{ fontWeight:600, fontSize:'0.8125rem' }}>
                              {r.report_type === 'SOFT_SKILLS' ? 'Soft Skills' : 'Hard Skills'}
                            </div>
                            <div style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>
                              {new Date(r.created_at).toLocaleDateString('fr-FR')}
                            </div>
                          </td>
                          <td style={{ fontWeight:700, color: r.note_sur_10 >= 7 ? '#16A34A' : r.note_sur_10 >= 5 ? '#F59E0B' : '#DC2626' }}>
                            {r.note_sur_10 != null ? `${r.note_sur_10}/10` : '—'}
                          </td>
                          <td><span style={{ padding:'2px 8px', borderRadius:20, fontSize:'0.7rem', fontWeight:700, background:rs.bg, color:rs.color }}>{rs.label}</span></td>
                          <td>
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/rt-report/${r.ao_id}/${r.candidate_id}`)}>
                              <Eye size={13}/> Voir
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Historique des actions */}
      <div>
        <h2 style={{ fontSize:'0.9375rem', fontWeight:700, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
          <Activity size={16} color="#6366f1"/> Historique des actions
        </h2>
        {loading ? (
          <div className="card" style={{ padding:32, textAlign:'center' }}><div className="spinner"/></div>
        ) : activityFeed.length === 0 ? (
          <div className="card" style={{ padding:32, textAlign:'center', color:'var(--text-muted)', fontSize:'0.875rem' }}>
            <Activity size={32} style={{ opacity:0.3, marginBottom:8 }}/>
            <div>Aucune action enregistrée</div>
          </div>
        ) : (
          <div className="card" style={{ padding:'4px 0' }}>
            {activityFeed.map((ev, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'12px 20px', borderBottom: i < activityFeed.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{
                  width:32, height:32, borderRadius:'50%', flexShrink:0,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  background: ev.type === 'submitted' ? 'rgba(34,197,94,0.08)' : 'var(--orange-50)',
                  color: ev.type === 'submitted' ? '#16A34A' : '#FF4100',
                }}>
                  {ev.type === 'submitted' ? <CheckCircle size={15}/> : <FileText size={15}/>}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'0.875rem', fontWeight:600, color:'var(--text-primary)' }}>
                    {ev.label}
                    {ev.note != null && <span style={{ marginLeft:8, padding:'1px 8px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:'rgba(34,197,94,0.08)', color:'#16A34A' }}>{ev.note}/10</span>}
                  </div>
                  <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2, display:'flex', gap:12 }}>
                    <span>{new Date(ev.date).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}</span>
                    {ev.aoId && (
                      <button className="btn btn-ghost btn-sm" style={{ padding:'0 4px', height:'auto', fontSize:'0.75rem', color:'#FF4100' }}
                        onClick={() => navigate(`/rt-report/${ev.aoId}/${ev.candidateId}`)}>
                        Voir rapport →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
